import axios from "axios";

// Create an axios instance with default config
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add a request interceptor to include auth token in all requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle common errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle 401 Unauthorized errors (token expired, etc.)
    if (error.response && error.response.status === 401) {
      // Clear local storage and redirect to login
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      alert("Please Login Again!!!");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// Export common API methods
export const apiService = {
  // Auth methods
  login: (credentials) => api.post("/login", credentials),
  register: (userData) => api.post("/register", userData),
  googleLogin: (data) => api.post(`/google`, data),
  firebaseLogin: (data) => api.post(`/firebase`, data),
  // register: (userData) => {
  //   return axios.post(`${API_URL}/auth/register`, userData, {
  //     headers: {
  //       'Content-Type': 'application/json'
  //     }
  //   });
  // },

  // Document methods
  getDocuments: () => api.get("/documents"),
  getDocument: (id) => api.get(`/documents/${id}`),
  updateDocument: (id, data) => api.put(`/documents/${id}`, data),
  deleteDocument: (id) => api.delete(`/documents/${id}`),
  getDocumentByStudentBatch: () => {
    return api.get(`/documents/studentBatches`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },
  getImageDocumentByStudentBatch: () =>
    api.get(`/document-images/studentBatches`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    }),

  // Image Document Methos
  getImageDocuments: () => api.get("/document-images"), // Get All Image Documents
  getImageDocument: (id) =>
    api.get(`/document-images/${id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    }),
  updateImageDocument: (id, data) => {
    const isFormData = data instanceof FormData;
    return api.put(`/document-images/${id}`, data, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "Content-Type": isFormData ? "multipart/form-data" : "application/json",
      },
    });
  },
  deleteImageDocument: (id) => api.delete(`/document-images/${id}`),

  getImageDocumentById: (id) =>
    api.get(`/document-images/teacher-document/${id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    }),
  getImageDocumentDemo: () =>
    api.get(`/document-images/teacher-document/demo`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    }),

  // Teacher document methods
  getTeacherDocuments: () => api.get("/documents/teacher-documents"),
  getTeacherImageDocuments: () => api.get("/document-images/teacher-documents"),

  getDashboardDataForTeacher: () => {
    return api.get("/teachers/dashboard", {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },

  // Scan document
  scanDocument: (formData) => {
    return api.post("/documents/scan", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },

  // Compare documents
  compareDocuments: (data) => api.post("/documents/compare", data),
  compareDocumentsImage: (data) => api.post("/document-images/compare", data),
  compareDocumentsImageDemo: (data) => api.post("/document-images/compare-demo", data),

  // Write document
  writeDocument: (data) => api.post("/documents/write", data),

  // IMage Documnet
  scanImageDocument: (formData) => {
    return api.post("/document-images/scan", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },
  // Image Document End

  // Get all teachers
  getTeachers: () => {
    return api.get("/teachers", {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },

  // Add a new teacher
  addTeacher: (teacherData) => {
    return api.post("/teachers", teacherData, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },

  // Update a teacher
  updateTeacher: (id, teacherData) => {
    return api.put(`/teachers/${id}`, teacherData, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },

  // Delete a teacher
  deleteTeacher: (id) => {
    return api.delete(`/teachers/${id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },
  // Student management API methods
  getStudents: () => {
    return api.get("/students", {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },

  getStudentDashboard: () => {
    return api.get("/students/dashboard", {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },

  getTeachetBatchStudents: (batch) => {
    return api.get(`/teachers/getTeachetBatchStudents/${batch}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },

  getStudentPerformance: (batch, date) => {
    return api.get(`/teachers/${batch}/performance/${date}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },

  getStudentByRollNumber: (rollNumber) => {
    return api.get(`/teachers/students/rollNumber/${rollNumber}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },

  addStudent: (studentData) => {
    return api.post("/students", studentData, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },

  updateStudent: (id, studentData) => {
    return api.put(`/students/${id}`, studentData, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },

  deleteStudent: (id) => {
    return api.delete(`/students/${id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },

  // Batch management API methods
  getActiveBatches: () => {
    return api.get("/batches/active", {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },

  // Audio
  getAudioExercises: () => {
    return api.get("/audio-exercise-levels", {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },

  scanAudioDocument: (formData) => {
    return api.post("/document-audio/scan", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },
  getAudioDocument: (id) => {
    return api.get(`/document-audio/${id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },
  getAudioDocumentForStudent: (id) => {
    return api.get(`/document-audio/${id}/for-student`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },
  getAudioDocumentForStudentdemo: () => {
    return api.get(`/document-audio/demo/for-student`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },
  getAudioDocumentWithComparison: (id) => {
    return api.get(`/document-audio/${id}/comparison`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },
  getAudioDocuments: () => {
    return api.get("/document-audio", {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },
  deleteAudioDocument: (id) => {
    return api.delete(`/document-audio/${id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },
  updateAudioDocument: (id, data) => {
    return api.put(`/document-audio/${id}`, data, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },
  getTeacherAudioDocuments: (id) => {
    return api.get("/document-audio/teacher-documents-by-category/" + id, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },

  getActiveTeacherBatches: () => {
    return api.get("/batches/teacher/active", {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },

  // Audio Student
  getAudionDocumentBycategory: (id) => {
    return api.get(`/document-audio/student-category-documents/${id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },

  compareAudioDocuments: (data) => {
    return api.post("/document-audio/compare", data, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },
  compareAudioDocumentsDemo: (data) => {
    return api.post("/document-audio/compare-demo", data, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },

  getBatches: () => {
    return api.get("/batches", {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },

  createBatch: (batchData) => {
    return api.post("/batches", batchData, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },

  updateBatch: (id, batchData) => {
    return api.put(`/batches/${id}`, batchData, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },

  deleteBatch: (id) => {
    return api.delete(`/batches/${id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },

  getBatchStudents: (batchId) => {
    return api.get(`/batches/${batchId}/students`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },

  addStudentToBatch: (batchId, studentId) => {
    return api.post(
      `/batches/${batchId}/students`,
      { studentId },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );
  },

  removeStudentFromBatch: (batchId, studentId) => {
    return api.delete(`/batches/${batchId}/students/${studentId}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },
  // Add this method to your apiService object
  getDocumentWithComparison: (id) => {
    return api.get(`/documents/${id}/comparison`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },
  getImageDocumentWithComparison: (id) => {
    return api.get(`/document-images/${id}/comparison`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },
  // Add this method to your apiService object
  updateUserStatus: (userId, status) => {
    return api.put(
      `/admin/users/${userId}/status`,
      { status },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );
  },
  // Add these methods to your apiService object

  getStudentAudioDocuments: (studentId) => api.get(`/teachers/students/${studentId}/documents-audio`),

  // Student attendance methods
  markAttendance: () => {
    return api.post(
      "/attendance/mark",
      {},
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );
  },

  getAttendanceHistory: () => {
    return api.get(`/attendance/history`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },

  // Admin attendance methods
  getPendingAttendances: () => {
    return api.get(`/attendance/pending`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },

  updateAttendanceStatus: (attendanceId, status, notes) => {
    return api.put(
      `/attendance/${attendanceId}/status`,
      { status, notes },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );
  },

  getAttendanceStats: (startDate, endDate) => {
    return api.get(`/attendance/stats`, {
      params: { startDate, endDate },
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },

  getUserProfile: () => {
    return api.get(`/users/profile`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },

  updateStudentProfile: (profileData) => {
    return api.put(`/students/profile`, profileData, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },

  updateTeacherProfile: (profileData) => {
    return api.put(`/teachers/profile`, profileData, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },
  contactUs: (data) => {
    return api.post("/contact", data);
  },
  contactList: () => {
    return api.get("/contact", {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },
  contactUpdate: (contactId, data) => {
    return api.patch(`/contact/${contactId}`, data, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },
  contactDelete: (contactId) => {
    return api.delete(`/contact/${contactId}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },

  getStudentDocuments: (studentId) => api.get(`/teachers/students/${studentId}/documents`),
  getStudentImageDocuments: (studentId) => api.get(`/teachers/students/${studentId}/documents-image`),
  getStudent: (studentId) => api.get(`/teachers/students/${studentId}`),
  getStudentDocumentComparison: (studentId, documentId) => api.get(`/teachers/students/${studentId}/documents/${documentId}/comparison`),
  getStudentImageDocumentComparison: (studentId, documentId) => api.get(`/teachers/students/${studentId}/documents-image/${documentId}/comparison`),

  getStudentAudioDocumentComparison: (studentId, documentId) => api.get(`/teachers/students/${studentId}/documents-audio/${documentId}/comparison`),

  // Subscription methods
  AddSubscription: (data) =>
    api.post("/subscriptions/admin/add", data, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    }),
  getSubscriptionPlans: () => api.get("/subscriptions/plans"),
  createRazorpayOrder: (planType) => api.post("/subscriptions/create-order", { planType }),
  verifyPayment: (paymentData) => api.post("/subscriptions/verify-payment", paymentData),
  getMySubscription: () => api.get("/subscriptions/my-subscription"),
  cancelSubscription: () => api.patch("/subscriptions/cancel"),
  renewSubscription: (paymentData) => api.post("/subscriptions/renew", paymentData),

  // Subscription related API methods
  getStudentSubscriptionDetails: () => {
    return api.get("/students/subscription-details", {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },

  getSubscriptionHistory: () => {
    return api.get("/subscriptions/history", {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },

  getAdminStudents: () => {
    return api.get("/students", {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },

  getAdminActiveSubscriptionStudentList: () => {
    return api.get("/subscriptions/studentActive", {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },

  getAdminStudentSubscriptionDetails: (studentId) => {
    let url = `/subscriptions/all`;
    if (studentId) {
      url = `/subscriptions/all/${studentId}`;
    } else {
      url = `/subscriptions/all`;
    }

    return api.get(url, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },
  getAdminSubscriptioList: (params) => {
    return api.get("/subscriptions/subscriptions?" + params, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },

  // PLAN MNG
  plans: () => {
    return api.get("/plans");
  },
  plan: (id) => {
    return api.get("/plans/" + id);
  },
  planEdit: (id, data) => {
    return api.put("/plans/" + id, data, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },
  planAdd: (data) => {
    return api.post("/plans", data, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },
  planDelete: (id) => {
    return api.delete("/plans/" + id, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },

  // PROMPT MNG
  getPromtList: () => {
    return api.get("/prompt", {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },
  addPrompt: (data) => {
    return api.post("/prompt", data, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },
  updatePrompt: (id, data) => {
    return api.put("/prompt/" + id, data, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },
  deletePrompt: (id) => {
    return api.delete("/prompt" + id, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  },
};

export default api;
